import React from "react";
import { hasAny, hasAll } from "../../utils/permissions";

/**
 * PermissionGate
 * ------------------------------------------------------------------
 * Renders children if:
 *  - user has ANY of `any` (if provided), AND
 *  - user has ALL of `all` (if provided), AND
 *  - `when` (boolean or () => boolean) evaluates true
 *
 * PROPS:
 * @param {Object}                            props
 * @param {string|string[]|function=}         props.any       // PermissionType | PermissionType[] | () => (PermissionType|PermissionType[])
 * @param {string|string[]|function=}         props.all       // PermissionType | PermissionType[] | () => (PermissionType|PermissionType[])
 * @param {boolean|function=}                 props.when      // booolean or () => boolean
 * @param {React.ReactNode=}                  props.fallback
 * @param {React.ReactNode}                   props.children
 *
 * USAGE:
 *  <PermissionGate any={[PermissionType.ManageAdminUsers]}>
 *    <Button>Manage Users</Button>
 *  </PermissionGate>
 *
 *  <PermissionGate all={[PermissionType.ManageResellers, PermissionType.ViewReports]}>
 *    <ReportsPanel />
 *  </PermissionGate>
 *
 *  <PermissionGate
 *    any={[PermissionType.AccessInvoices]}
 *    when={resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable}
 *  >
 *    <NavLink to="/billing" title={getPermissionDescription(PermissionType.AccessInvoices)}>
 *      Billing
 *    </NavLink>
 *  </PermissionGate>
 */
export default function PermissionGate({ any = [], all = [], when = true, fallback = null, children }) {

  function resolveList(input) {
    try {
      const value = typeof input === "function" ? input() : input;
      if (value === null) {
        return [];
      }

      if (Array.isArray(value)) {
        return value;
      }

      return [value];
    } catch {
      return [];
    }
  }

  const anyList = resolveList(any);
  const allList = resolveList(all);

  const okAny = anyList.length ? hasAny(...anyList) : true;
  const okAll = allList.length ? hasAll(...allList) : true;
  const okWhen = typeof when === "function" ? !!when() : !!when; 
  return okAny && okAll && okWhen ? <>{children}</> : <>{fallback}</>;
}

export { PermissionType, getPermissionDescription, hasAny, hasAll, hasPermission } from "../../utils/permissions";